import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from './auth';
import { ErrorBanner, LoadingPane, PageHeader } from './components/page-elements';

const TIMEZONE_FALLBACK = [
  'America/Chicago',
  'America/New_York',
  'America/Denver',
  'America/Los_Angeles',
  'UTC',
];

const getTimeZoneOptions = () => {
  try {
    if (typeof Intl?.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch {
    // ignore and use fallback
  }
  return TIMEZONE_FALLBACK;
};

const Profile = () => {
  const localUser = useMemo(() => getStoredUser(), []);
  const isAdmin = localUser?.role === 'admin';
  const timezoneOptions = useMemo(() => getTimeZoneOptions(), []);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    customerName: '',
    isActive: true,
    createdAt: '',
    timeZone: 'America/Chicago',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    weekStartsOn: 'monday',
    notifyNewOrders: true,
    notifySound: true,
    notifyDesktop: false,
    receiveBols: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  useEffect(() => {
    if (!localUser?._id) {
      setLoading(false);
      return;
    }

    fetch(`${API_BASE_URL}/api/users/${localUser._id}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load profile');
        return res.json();
      })
      .then((data) => {
        setFormData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          role: data.role || '',
          customerName: data.customerName?.customerName || '',
          isActive: data.isActive !== false,
          createdAt: data.createdAt || '',
          timeZone: data.timeZone || 'America/Chicago',
          dateFormat: data.dateFormat || 'MM/DD/YYYY',
          timeFormat: data.timeFormat || '12h',
          weekStartsOn: data.weekStartsOn || 'monday',
          notifyNewOrders: data.notifyNewOrders !== false,
          notifySound: data.notifySound !== false,
          notifyDesktop: Boolean(data.notifyDesktop),
          receiveBols: Boolean(data.receiveBols),
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [localUser?._id]);

  useEffect(() => {
    if (!isAdmin) return;

    setCustomersLoading(true);
    fetch(`${API_BASE_URL}/api/customers`, {
      headers: getAuthHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load customers');
        return res.json();
      })
      .then((data) => {
        setCustomers(Array.isArray(data) ? data : []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setCustomersLoading(false));
  }, [isAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = (name) => (e) => {
    setFormData((prev) => ({ ...prev, [name]: Boolean(e.target.checked) }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!localUser?._id) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${localUser._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          timeZone: formData.timeZone,
          dateFormat: formData.dateFormat,
          timeFormat: formData.timeFormat,
          weekStartsOn: formData.weekStartsOn,
          notifyNewOrders: formData.notifyNewOrders,
          notifySound: formData.notifySound,
          notifyDesktop: formData.notifyDesktop,
          receiveBols: formData.receiveBols,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to update profile');
      }

      const updatedUser = data.user || {};
      const existing = getStoredUser() || {};
      localStorage.setItem(
        'user',
        JSON.stringify({
          ...existing,
          firstName: updatedUser.firstName ?? formData.firstName,
          lastName: updatedUser.lastName ?? formData.lastName,
          fullName: `${updatedUser.firstName ?? formData.firstName} ${updatedUser.lastName ?? formData.lastName}`.trim(),
          timeZone: updatedUser.timeZone ?? formData.timeZone,
          dateFormat: updatedUser.dateFormat ?? formData.dateFormat,
          timeFormat: updatedUser.timeFormat ?? formData.timeFormat,
          weekStartsOn: updatedUser.weekStartsOn ?? formData.weekStartsOn,
          notifyNewOrders: updatedUser.notifyNewOrders ?? formData.notifyNewOrders,
          notifySound: updatedUser.notifySound ?? formData.notifySound,
          notifyDesktop: updatedUser.notifyDesktop ?? formData.notifyDesktop,
          receiveBols: updatedUser.receiveBols ?? formData.receiveBols,
        })
      );

      setSuccess('Profile updated successfully.');
    } catch (err) {
      setError(err.message);
    }

    setSaving(false);
  };

  if (!localUser) {
    return <Typography>Please log in to update your profile.</Typography>;
  }

  if (loading) {
    return <LoadingPane label="Loading profile..." />;
  }

  return (
    <Box p={2}>
      <PageHeader
        title="Profile & Settings"
        subtitle="Review account details, timezone preferences, and notification settings."
      />

      <ErrorBanner message={error} />
      {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Personal Information
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Box component="form" onSubmit={handleSave}>
                <Grid container spacing={1.2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Email" name="email" value={formData.email} fullWidth disabled />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ fontWeight: 700, mt: 2.5, mb: 1 }}>
                  Preferences
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Grid container spacing={1.2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="profile-timezone-label">Time Zone</InputLabel>
                      <Select
                        labelId="profile-timezone-label"
                        name="timeZone"
                        value={formData.timeZone}
                        label="Time Zone"
                        onChange={handleChange}
                      >
                        {timezoneOptions.map((tz) => (
                          <MenuItem key={tz} value={tz}>
                            {tz}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel id="profile-date-format-label">Date Format</InputLabel>
                      <Select
                        labelId="profile-date-format-label"
                        name="dateFormat"
                        value={formData.dateFormat}
                        label="Date Format"
                        onChange={handleChange}
                      >
                        <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                        <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                        <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel id="profile-time-format-label">Time Format</InputLabel>
                      <Select
                        labelId="profile-time-format-label"
                        name="timeFormat"
                        value={formData.timeFormat}
                        label="Time Format"
                        onChange={handleChange}
                      >
                        <MenuItem value="12h">12-hour</MenuItem>
                        <MenuItem value="24h">24-hour</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel id="profile-week-start-label">Week Starts On</InputLabel>
                      <Select
                        labelId="profile-week-start-label"
                        name="weekStartsOn"
                        value={formData.weekStartsOn}
                        label="Week Starts On"
                        onChange={handleChange}
                      >
                        <MenuItem value="monday">Monday</MenuItem>
                        <MenuItem value="sunday">Sunday</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ fontWeight: 700, mt: 2.5, mb: 1 }}>
                  Notifications
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Stack>
                  <FormControlLabel
                    control={<Switch checked={Boolean(formData.notifyNewOrders)} onChange={handleToggle('notifyNewOrders')} />}
                    label="Enable new submitted order notifications"
                  />
                  <FormControlLabel
                    control={<Switch checked={Boolean(formData.notifySound)} onChange={handleToggle('notifySound')} />}
                    label="Play notification sound"
                  />
                  <FormControlLabel
                    control={<Switch checked={Boolean(formData.notifyDesktop)} onChange={handleToggle('notifyDesktop')} />}
                    label="Allow desktop/browser notifications"
                  />
                  <FormControlLabel
                    control={<Switch checked={Boolean(formData.receiveBols)} onChange={handleToggle('receiveBols')} />}
                    label="Receive completed BOL emails"
                  />
                </Stack>

                <Box mt={2}>
                  <Button type="submit" variant="contained" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Profile'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Account Summary
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Stack spacing={0.8}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Role</Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                    {formData.role || 'N/A'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Company</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formData.customerName || 'N/A'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip size="small" label={formData.isActive ? 'Active' : 'Inactive'} color={formData.isActive ? 'success' : 'default'} />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Created</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formData.createdAt ? new Date(formData.createdAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {isAdmin && (
        <Box mt={3}>
          <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                All Customers
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {customersLoading ? (
                <Typography>Loading customers...</Typography>
              ) : customers.length === 0 ? (
                <Typography>No customers found.</Typography>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid var(--ui-border)', borderRadius: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Company Name</TableCell>
                        <TableCell>Customer Code</TableCell>
                        <TableCell>City</TableCell>
                        <TableCell>State</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customers.map((customer) => (
                        <TableRow key={customer._id}>
                          <TableCell>{customer.customerName || 'N/A'}</TableCell>
                          <TableCell>{customer.customerCode || 'N/A'}</TableCell>
                          <TableCell>{customer.customerCity || 'N/A'}</TableCell>
                          <TableCell>{customer.customerState || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default Profile;

